import { useMemo } from 'react';
import type { RemittanceRecord } from '@dill/dill-shared';
import { recordUnappliedAmount } from '@dill/dill-shared';
import { DillTable } from '../../../general/Dill';
import type { DillColumn } from '../../../general/Dill';
import { formatCurrency } from '../utils';

/**
 * What Dill proposed for one payment against what was actually posted.
 *
 * Per record rather than per receipt: a receipt covers every payment in one approval, and those
 * payments are for different customers with different invoices. One combined table would read as a
 * single application and could not be reconciled against any one cheque.
 *
 * The comparison is the audit trail — it is what says a human changed the suggestion — so both
 * columns stay even when they agree.
 */
interface AllocationRow {
  id: string;
  docNumber: string;
  suggested: number | null;
  applied: number | null;
  isNew: boolean;
  isRemoved: boolean;
  isChanged: boolean;
  /** The remainder left on the customer's account. Not an invoice, but part of the same payment. */
  isOpenCredit: boolean;
}

/** Synthetic row id. No invoice can collide with it, since invoice ids are ERP-issued. */
const OPEN_CREDIT_ROW_ID = '__open_credit__';

export const RecordAllocationTable = ({ record }: { record: RemittanceRecord }) => {
  const openCredit = recordUnappliedAmount(record);

  const rows: AllocationRow[] = useMemo(() => {
    const suggestedById = new Map(
      record.matchedInvoices.map((invoice) => [invoice.invoiceId, invoice])
    );
    const appliedById = new Map(
      record.appliedInvoices.map((allocation) => [allocation.invoiceId, allocation])
    );
    const allIds = new Set([...suggestedById.keys(), ...appliedById.keys()]);

    const built: AllocationRow[] = Array.from(allIds).map((invoiceId) => {
      const suggested = suggestedById.get(invoiceId);
      const applied = appliedById.get(invoiceId);
      const suggestedAmount = suggested?.suggestedAmount ?? null;
      const appliedAmount = applied?.amountApplied ?? null;
      return {
        id: invoiceId,
        docNumber: applied?.docNumber ?? suggested?.docNumber ?? invoiceId,
        suggested: suggestedAmount,
        applied: appliedAmount,
        isNew: !!applied && !suggested,
        isRemoved: !!suggested && !applied,
        isChanged:
          !!suggested &&
          !!applied &&
          suggestedAmount !== null &&
          appliedAmount !== null &&
          Math.abs(suggestedAmount - appliedAmount) > 0.005,
        isOpenCredit: false,
      };
    });

    // The remainder is part of the payment and was posted to the ERP as its own document, so a
    // table that stops at the invoices does not add up: four lines totalling $379.60 under a
    // heading that says $930.00 reads as a bug in the post rather than money left on account.
    if (openCredit > 0.005) {
      built.push({
        id: OPEN_CREDIT_ROW_ID,
        docNumber: 'Open credit',
        suggested: null,
        applied: openCredit,
        isNew: false,
        isRemoved: false,
        isChanged: false,
        isOpenCredit: true,
      });
    }

    return built;
  }, [record.matchedInvoices, record.appliedInvoices, openCredit]);

  // Reconciles to the payment, which is the number the reader is checking against.
  const totalPosted =
    record.appliedInvoices.reduce((sum, allocation) => sum + allocation.amountApplied, 0) +
    openCredit;

  const columns: DillColumn<AllocationRow>[] = useMemo(
    () => [
      {
        key: 'docNumber',
        label: 'Invoice',
        width: 160,
        render: (_v, row) => (
          <span className="flex items-center gap-1.5">
            <span className={row.isOpenCredit ? 'italic text-gray-500' : undefined}>
              {row.docNumber}
            </span>
            {row.isOpenCredit && (
              <span className="text-[9px] font-bold text-emerald-700 uppercase bg-emerald-100 rounded px-1 py-px">
                on account
              </span>
            )}
            {row.isNew && (
              <span className="text-[9px] font-bold text-blue-600 uppercase bg-blue-100 rounded px-1 py-px">
                new
              </span>
            )}
            {row.isRemoved && (
              <span className="text-[9px] font-bold text-red-500 uppercase bg-red-100 rounded px-1 py-px">
                removed
              </span>
            )}
            {row.isChanged && (
              <span className="text-[9px] font-bold text-amber-600 uppercase bg-amber-100 rounded px-1 py-px">
                edited
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'suggested',
        label: 'Suggested',
        width: 110,
        numeric: true,
        render: (_v, row) => (
          <span className={row.isRemoved ? 'text-gray-400 line-through' : 'text-gray-500'}>
            {row.suggested !== null ? formatCurrency(row.suggested) : '—'}
          </span>
        ),
      },
      {
        key: 'applied',
        label: 'Applied',
        width: 110,
        numeric: true,
        render: (_v, row) => (
          <span
            className={
              'font-medium ' +
              (row.isOpenCredit
                ? 'text-emerald-700'
                : row.isNew
                ? 'text-blue-700'
                : row.isRemoved
                ? 'text-red-400'
                : row.isChanged
                ? 'text-amber-700'
                : 'text-gray-900')
            }>
            {row.applied !== null ? formatCurrency(row.applied) : '—'}
          </span>
        ),
        footer: <span className="font-semibold tabular-nums">{formatCurrency(totalPosted)}</span>,
      },
    ],
    [totalPosted]
  );

  if (rows.length === 0) {
    return (
      <p className="px-1 py-2 text-[11px] text-gray-400">
        No invoices were allocated — posted as unapplied cash on the customer&apos;s account.
      </p>
    );
  }

  return (
    <DillTable<AllocationRow>
      columns={columns}
      data={[...rows]}
      rowKey={(row) => row.id}
      search={false}
      sortable={false}
      showCount={false}
      showFooter={true}
      maxHeight="200px"
      striped={false}
      tableClassName="text-xs"
      rowClassName={(row) =>
        row.isNew
          ? 'bg-blue-50/40'
          : row.isRemoved
          ? 'bg-red-50/30'
          : row.isChanged
          ? 'bg-amber-50/40'
          : ''
      }
    />
  );
};
